import React from 'react';
import { EventContentArg } from '@fullcalendar/react';
import { useStoreState } from 'hooks/useStoreState';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { View } from '../../../../constants/Interfaces';
import './EventCard.scss';

interface IProps {
  eventInfo: any;
  tooltipId: string;
  isBlockTime: boolean;
  eventContent: EventContentArg;
  activeView?: View;
}

enum CalendarIntervals {
  Min15 = 15,
  Min30 = 30,
  Min60 = 60,
}

export const EventCard: React.FC<IProps> = ({
  isBlockTime,
  tooltipId,
  eventInfo,
  eventContent,
  activeView,
}) => {
  const { t } = useTranslation();

  const clientName = !eventInfo.clientName.trim()
    ? t('reservation_client_incognito')
    : eventInfo.clientName;

  const reservationSettingsState = useStoreState().reservationSettingsState;
  const { totalPersonsCount, employeeMaxPersons } = eventInfo;

  const getMinTimeToChangeNameStyles = () => {
    const calendarInterval =
      reservationSettingsState?.data?.webReservationCalendarInterval;
    let minutes;
    if (calendarInterval && calendarInterval === CalendarIntervals.Min60) {
      minutes = 70;
    } else if (calendarInterval && calendarInterval === CalendarIntervals.Min30) {
      minutes = 30;
    } else {
      minutes = 20;
    }
    return minutes;
  };

  const clientNameHTML = () => {
    if ((employeeMaxPersons > 1 && !isBlockTime) || activeView === View.month)
      return null;

    return (
      <div
        style={{
          fontWeight: isBlockTime ? 400 : 700,
          marginRight: 5,
        }}
      >
        {` ${isBlockTime ? t('reservation_blocked_time') : clientName}`}
      </div>
    );
  };

  const peopleCountHTML = () => {
    if (employeeMaxPersons <= 1 || isBlockTime) return null;

    return (
      <div>
        {t('label_busyness')}: {Number(totalPersonsCount)}/
        {Math.max(Number(employeeMaxPersons), Number(totalPersonsCount))}
      </div>
    );
  };

  const serviceNameHTML = () => {
    return <div className='event-content-service'>{eventInfo.serviceName}</div>;
  };

  const reservationTimeHTML = () => {
    let eventTime = eventContent.timeText;

    if (activeView === View.month) {
      const startTime = moment(eventInfo.start).format('HH:mm');
      const endTime = moment(eventInfo.end).format('HH:mm');
      eventTime = `${startTime} – ${endTime}`;
    }

    return (
      <div style={eventInfo.duration < 15 ? { marginRight: 5 } : {}}>
        {`${eventTime} `}
      </div>
    );
  };

  return (
    <div
      data-for={tooltipId}
      data-tip={JSON.stringify(eventInfo)}
      className={`event-content ${
        activeView === View.month ? 'event-content--month' : ''
      }`}
      style={{ backgroundColor: eventContent.event.backgroundColor }}
    >
      <div
        className={
          eventInfo.duration < 15 ? 'event-content-flow event-content-flow--column' : ''
        }
      >
        <div
          className={
            eventInfo.duration < getMinTimeToChangeNameStyles()
              ? 'event-content-flow event-content-flow--row'
              : 'event-content-flow event-content-flow--column'
          }
        >
          {reservationTimeHTML()}
          {clientNameHTML()}
          {serviceNameHTML()}
        </div>

        {peopleCountHTML()}
      </div>
    </div>
  );
};
